import { LitElement, html } from 'lit';
import { NotificationBus } from '../services/notification-bus.js';
import './icons/error-icon.js';
import './icons/success-icon.js';
import './icons/warning-icon.js';
import './icons/info-icon.js';
import './icons/close-icon.js';

/**
 * Notification Toast Component
 * Displays toast notifications in the top-right corner of the screen
 * Listens for notification events from NotificationBus
 *
 * Features:
 * - Auto-dismiss after specified duration
 * - Manual dismiss via close button
 * - Stacked notifications (queue support)
 * - DaisyUI alert styling
 */
class NotificationToast extends LitElement {
    static properties = {
        notifications: { type: Array, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.notifications = [];
        this._timers = new Map(); // Track auto-dismiss timers
    }

    connectedCallback() {
        super.connectedCallback();
        this._boundHandler = this._handleNotification.bind(this);
        window.addEventListener(NotificationBus.EVENT_NAME, this._boundHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener(NotificationBus.EVENT_NAME, this._boundHandler);

        // Clear all timers
        this._timers.forEach((timer) => clearTimeout(timer));
        this._timers.clear();
    }

    /**
     * Handle incoming notification event
     * @param {CustomEvent} event - Notification event
     */
    _handleNotification(event) {
        const { message, type, duration, id, source } = event.detail;

        // Add to notification queue
        this.notifications = [...this.notifications, { message, type, id, source }];

        // Set auto-dismiss timer only if duration is provided and > 0
        // duration: 0 or null = persistent notification (manual dismiss only)
        if (duration && duration > 0) {
            const timer = setTimeout(() => {
                this.dismiss(id);
            }, duration);

            this._timers.set(id, timer);
        }

        // Log in dev mode
        if (import.meta.env.DEV) {
            console.log(
                `[NOTIFICATION] ${type.toUpperCase()}:`,
                message,
                source ? `(source: ${source})` : '',
                duration ? `(auto-dismiss: ${duration}ms)` : '(persistent)'
            );
        }
    }

    /**
     * Dismiss a notification
     * @param {number} id - Notification ID
     */
    dismiss(id) {
        // Clear timer if exists
        const timer = this._timers.get(id);
        if (timer) {
            clearTimeout(timer);
            this._timers.delete(id);
        }

        // Remove from queue
        this.notifications = this.notifications.filter((n) => n.id !== id);
    }

    /**
     * Get icon for notification type
     * @param {string} type - Notification type
     * @returns {import('lit').TemplateResult}
     */
    _getIcon(type) {
        const iconClass = 'shrink-0';

        switch (type) {
            case 'error':
                return html`<error-icon class="${iconClass}"></error-icon>`;
            case 'success':
                return html`<success-icon class="${iconClass}"></success-icon>`;
            case 'warning':
                return html`<warning-icon class="${iconClass}"></warning-icon>`;
            case 'info':
            default:
                return html`<info-icon class="${iconClass}"></info-icon>`;
        }
    }

    /**
     * Get alert CSS classes for notification type
     * Returns complete class string to help Tailwind detect classes during content scan
     * @param {string} type - Notification type
     * @returns {string}
     */
    _getAlertClasses(type) {
        const baseClasses =
            'alert shadow-lg flex flex-row items-start sm:items-center gap-3 p-4 w-full max-w-md';

        // Explicitly list all alert types so Tailwind can detect them during content scan
        switch (type) {
            case 'error':
                return `${baseClasses} alert-error`;
            case 'success':
                return `${baseClasses} alert-success`;
            case 'warning':
                return `${baseClasses} alert-warning`;
            case 'info':
            default:
                return `${baseClasses} alert-info`;
        }
    }

    render() {
        if (this.notifications.length === 0) {
            return html``;
        }

        return html`
            <div class="toast toast-top toast-end z-[60] p-4">
                ${this.notifications.map(
                    (notification) => html`
                        <div class="${this._getAlertClasses(notification.type)}" role="alert">
                            ${this._getIcon(notification.type)}
                            <span
                                class="flex-grow min-w-0 text-sm text-left leading-relaxed break-words whitespace-normal"
                                >${notification.message}</span
                            >
                            <button
                                @click="${() => this.dismiss(notification.id)}"
                                class="btn btn-sm btn-ghost btn-circle ml-2 self-center"
                                aria-label="Dismiss notification"
                            >
                                <close-icon></close-icon>
                            </button>
                        </div>
                    `
                )}
            </div>
        `;
    }
}

customElements.define('notification-toast', NotificationToast);
